import { Component, useCallback } from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { deepEqual } from 'fast-equals';

import Rating from 'components/Rating';
import { constructMSAImageUrl, makeAscii, pluralize } from 'helpers';
import { isProductHearted, makeFavoriteHeartButton, makeHandleHeartButtonClick } from 'helpers/HeartUtils';
import ProductUtils from 'helpers/ProductUtils';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { trackEvent } from 'helpers/analytics';
import { SmallLoader } from 'components/Loader';
import SocialCollectionsWidget from 'components/account/SocialCollectionsWidget';
import ImageLazyLoader from 'components/common/ImageLazyLoader';
import marketplace from 'cfg/marketplace.json';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';
import { MoreInfo } from 'components/icons';
import Tooltip from 'components/common/Tooltip';

// order matters
import commonCardCss from 'styles/components/common/melodyCard.scss';
import css from 'styles/components/common/melodyCardProduct.scss';

const { melodyProductCard: {
  defaultNoBackground,
  defaultComponentStyle,
  forceHighRes,
  hideMsrp = false
} } = marketplace;

const RELATED_STYLE_IMG_SRC_PARAMS = { autoCrop: true, width: 42, height: 42 };
const RELATED_STYLE_IMG_RETINA_PARAMS = { autoCrop: true, width: 84, height: 84 };
const LOAD_THRESHOLD = 1.8;

const MakeSocialCollectionWidget = ({ styleId, isCollectionShown }) => {
  const getStyleId = useCallback(() => styleId, [styleId]);

  if (!(styleId && isCollectionShown)) {
    return null;
  }

  return (
    <div className={css.collection}>
      <SocialCollectionsWidget getStyleId={getStyleId} />
    </div>
  );
};

const CrossSiteTooltip = styleId => (
  <Tooltip
    tooltipId={`crossSite-${styleId}`}
    wrapperClassName={css.trustedRetailerTooltipWrapper}
    direction="bottom"
    content="Zappos has partnered with these retailers to give you more selection.">
    <MoreInfo/>
  </Tooltip>
);

/* eslint-disable css-modules/no-undef-class */
export const getComponentStyling = (componentStyle, showFavoriteHeart, hydraSearchColorSwatches, colorSwatchToggleState, relatedStyles, crossSite) => {
  const canUseRelatedStyles = colorSwatchToggleState && relatedStyles.length;
  switch (componentStyle) {
    case 'recommender':
    case 'productSearchNoStyle':
      return cn(
        css.melodyCardInCarousel,
        { [css.heartListContainer]: showFavoriteHeart }
      );
    case 'melodyCurated':
      return cn(
        css.melodyCurated,
        { [css.heartListContainer]: showFavoriteHeart }
      );
    case 'melodyGrid':
      return cn(
        css.melodyGrid,
        { [css.heartListContainer]: showFavoriteHeart }
      );
    case 'oos':
      return css.melodyCardOosModal;
    case 'melodySimple':
      return css.melodySimple;
    case 'fullBleed':
      return css.fullBleed;
    case 'searchProduct':
    case 'searchProductInlineRecos':
    case 'searchProductInlineVerticalRecos':
    case 'searchProductCrossSite':
      return cn(
        css.searchOverrides,
        { [css.hasHeart]: showFavoriteHeart },
        { [css.isCrossSite]: !!crossSite },
        { [css.hasColorSwatches]: hydraSearchColorSwatches > 0 },
        // This junk is temporary until we decide how we want to do color swatches
        { [css.hasRelatedStylesDropdownLarge]: (hydraSearchColorSwatches === 1 && canUseRelatedStyles) },
        { [css.hasRelatedStylesDropdownCompact]: (hydraSearchColorSwatches === 2 && canUseRelatedStyles) }
      );
    case 'brandTrendingStyle':
      return cn(
        { [css.heartListContainer]: showFavoriteHeart }
      );
    default:
      return null;
  }
};
/* eslint-disable css-modules/no-undef-class */

export const makeCachedRelatedStyles = ({ cardData, hydraSearchColorSwatches }) => {
  if (hydraSearchColorSwatches) {
    return cardData?.relatedStyles ? [ ...cardData.relatedStyles, cardData ] : [ cardData ];
  }
  return [];
};

export class MelodyCardProduct extends Component {
  static displayName = 'MelodyCardProduct';

  state = {
    hasProductBeenHovered: false,
    isProductFocused: false,
    isCollectionShown: false,
    selectedRelatedStyleIndex: null,
    // prevents jerkyness from reloading imgs on toggle
    relatedStylesDropdownHasBeenOpened: false,
    // only render links after hovered/focused to help SEO
    renderRelatedStyleLinks: false,
    // makes it so we only have to add the orginal product to the relatedStyles once.
    // this should never change.
    cachedRelatedStyles: makeCachedRelatedStyles(this.props)
  };

  shouldComponentUpdate(nextProps, nextState) {
    const { isProductFocused, selectedRelatedStyleIndex, relatedStylesDropdownHasBeenOpened,
      renderRelatedStyleLinks, hasProductBeenHovered, isCollectionShown } = this.state;
    const { colorSwatchToggleState, additionalClasses, msaImageParams, heartsData, isRelatedStyleDropdownOpen, cardData: { animationImages, price, originalPrice } } = this.props;
    const { hearts, heartsList } = heartsData || {};
    const { heartsData: nextHeartsData, cardData: nextCardData } = nextProps;
    const { hearts: nextHearts, heartsList: nextHeartsList } = nextHeartsData || {};

    if (animationImages?.length > 0) {
      return true;
    }
    return nextProps.colorSwatchToggleState !== colorSwatchToggleState
      || !deepEqual(nextProps.additionalClasses, additionalClasses)
      || !deepEqual(nextProps.msaImageParams, msaImageParams)
      || !deepEqual(nextHearts, hearts)
      || !deepEqual(nextHeartsList, heartsList)
      || nextProps.isRelatedStyleDropdownOpen !== isRelatedStyleDropdownOpen
      || nextState.isProductFocused !== isProductFocused
      || nextState.hasProductBeenHovered !== hasProductBeenHovered
      || nextState.selectedRelatedStyleIndex !== selectedRelatedStyleIndex
      || nextState.relatedStylesDropdownHasBeenOpened !== relatedStylesDropdownHasBeenOpened
      || nextState.renderRelatedStyleLinks !== renderRelatedStyleLinks
      || nextState.isCollectionShown !== isCollectionShown
      || nextCardData.price !== price
      || nextCardData.originalPrice !== originalPrice;
  }

  makeProductNameText = () => {
    const { cardData: { name, productName } } = this.props;
    return makeAscii(name || productName || '');
  };

  makeBrandNameText = () => {
    const { cardData: { brand, brandName } } = this.props;
    return makeAscii(brand || brandName || '');
  };

  makeBrandName = () => {
    const {
      context: { testId },
      props: { additionalClasses }
    } = this;
    const brandName = this.makeBrandNameText();
    return (brandName &&
      <p
        className={cn(css.brandName, additionalClasses.brandName)}
        itemProp="brand"
        itemScope
        itemType="http://schema.org/Brand"
        data-test-id={testId('brand')}>
        {/* This span is unfortunately necessary for the brand schema  */}
        <span itemProp="name">{brandName}</span>
      </p>
    );
  };

  makeProductName = () => {
    const { testId } = this.context;
    const productName = this.makeProductNameText();
    return (productName &&
      <p
        className={css.productName}
        itemProp="name"
        data-test-id={testId('productName')}>
        {productName}
      </p>
    );
  };

  makeColorName = () => {
    const { testId } = this.context;
    const { cardData: { colorName } } = this.props;
    return (colorName &&
      <p
        className={css.colorName}
        itemProp="color"
        data-test-id={testId('colorName')}>
        {makeAscii(colorName)}
      </p>
    );
  };

  lowestPriceFromRelatedStyles = () => {
    const { cardData: { price } } = this.props;
    const { cachedRelatedStyles } = this.state;
    const lowestPriceInt = cachedRelatedStyles.reduce((acc, curVal) => {
      const relatedPrice = ProductUtils.priceToFloat(curVal.price);
      return acc < relatedPrice ? acc : relatedPrice;
    }, ProductUtils.priceToFloat(price));
    return `$${lowestPriceInt.toFixed(2)}`;
  };

  makePrice = () => {
    const { testId } = this.context;
    const {
      cardData: { price, originalPrice },
      additionalClasses, hydraSearchColorSwatches,
      alwaysShowOriginalPrice
    } = this.props;
    const { cachedRelatedStyles } = this.state;
    if (price) {
      const isSale = ProductUtils.isStyleOnSale({ price, originalPrice });
      const msrp = (hideMsrp === true) ? null : <span className={css.msrpLabel}>MSRP: </span>;
      // do not use itemProp="price" because it is volatile
      return (
        <p className={cn(css.priceContainer, additionalClasses.priceContainer, { [css.alwaysShowOriginalPrice]: alwaysShowOriginalPrice })}>
          {hydraSearchColorSwatches && cachedRelatedStyles.length > 1 ? <>
            <span className={css.price} data-test-id={testId('price')}>From: {this.lowestPriceFromRelatedStyles()}</span>
          </> :
          <>
            <span className={cn(css.price, { [css.priceSale]: isSale })} data-test-id={testId('price')}>{price}</span>
            {isSale && <span className={css.originalPrice} data-test-id={testId('originalPrice')}>{msrp}{originalPrice}</span>}
          </>}
        </p>
      );
    }
    return null;
  };

  makeProductRating = () => {
    const { cardData: { productRating = '0', reviewCount }, showRatingStars, crossSite } = this.props;
    const showStars = typeof showRatingStars === 'boolean' ? showRatingStars : true;
    if (productRating !== '0' && showStars && !crossSite) {
      return (
        <p>
          <Rating
            rating={productRating}
            additionalClasses={css.fromMCP}
            reviewCount={reviewCount || 0}
            forceDesktopRating={true}
            fullSize={true} />
        </p>
      );
    }
    return null;
  };

  makeEventValue = () => {
    const { eventLabel, cardData: { productId, styleId, gae } } = this.props;
    if (eventLabel) {
      if (eventLabel === 'melodyGrid') {
        return gae;
      } else {
        return `product-${productId}${styleId ? `-style-${styleId}` : ''}`;
      }
    }
    return false;
  };

  makeProductBanner = () => {
    const { testId } = this.context;
    const { cardData: { isCouture, isNew, isSponsored, styleId }, crossSite } = this.props;
    if (crossSite) {
      return (
        <div data-test-id={testId('trustedRetailerBanner')} className={css.trustedRetailerBanner}>
          Trusted Retailer
          <CrossSiteTooltip styleId={styleId}/>
        </div>
      );
    } if (isCouture) {
      return <p className={css.luxBanner} data-test-id={testId('luxBanner')}>Luxury</p>;
    } else if (`${isNew}` === 'true') { // isNew not always be bool from patron TODO: is this still true given all the datasources this component pulls from?
      return <p className={css.newBanner} data-test-id={testId('newBanner')}>New</p>;
    } else if (isSponsored) {
      return <p className={css.sponsoredBanner} data-test-id={testId('sponsoredBanner')}>Sponsored</p>;
    }
    return null;
  };

  makeImageUrl = () => {
    const { msaImageParams, cardData: {
      imageId, mainImageId, thumbnailImageId, msaImageId, msaImageUrl, thumbnailImageUrl, src
    } } = this.props;
    const msaId = imageId || mainImageId || thumbnailImageId || msaImageId || null;
    if (forceHighRes) {
      const msaImg = msaId && msaImageParams ? constructMSAImageUrl(msaId, msaImageParams) : null;
      return msaImg || msaImageUrl || thumbnailImageUrl || src;
    }
    const msaImg = !msaImageUrl && msaId && msaImageParams ? constructMSAImageUrl(msaId, msaImageParams) : null;
    return msaImageUrl || msaImg || thumbnailImageUrl || src;
  };

  getSecondaryImageFromImageMap = imageMap => {
    const { PT01, PT03, TOPP } = imageMap || {};
    return TOPP || PT03 || PT01;
  };

  makeProductImage = () => {
    const { cachedRelatedStyles, selectedRelatedStyleIndex } = this.state;
    const {
      additionalClasses,
      animationTimerIndex,
      componentStyle,
      cardData: { alt, productName, name, styleColor, imageMap, animationImages },
      hydraPDPProductAngles,
      msaImageParams,
      shouldLazyLoad
    } = this.props;
    const image = this.makeImageUrl();
    const altString = alt === '' ? alt : alt || styleColor || productName || name;
    const imgProps = {
      src: image,
      alt: altString,
      className: additionalClasses.image
    };

    const IMAGE_PLACEHOLDER = <div className={css.imgPlaceholder}><SmallLoader/></div>;

    const forceLoad = !shouldLazyLoad && componentStyle === 'searchProduct';

    let secondaryImage = null;
    const chosenSecondaryImage = this.getSecondaryImageFromImageMap(imageMap);

    if ([1, 2].includes(hydraPDPProductAngles) && chosenSecondaryImage) {
      const secondaryImgProps = {
        src: constructMSAImageUrl(chosenSecondaryImage, msaImageParams),
        alt: altString,
        className: additionalClasses.image
      };
      secondaryImage = <ImageLazyLoader
        className={css.hoverImage}
        forceLoad={forceLoad}
        imgProps={secondaryImgProps}
        loadThreshold={LOAD_THRESHOLD}
        placeholder={IMAGE_PLACEHOLDER} />;
    }

    if (typeof selectedRelatedStyleIndex === 'number' && cachedRelatedStyles[selectedRelatedStyleIndex]) {
      // Show related product image when hovered
      const { msaImageId, styleColor } = cachedRelatedStyles[selectedRelatedStyleIndex];
      const url = constructMSAImageUrl(msaImageId, msaImageParams);
      return <span><img alt={`Alternate style: ${styleColor}`} src={url}/></span>;
    }
    // If the product has an animation, we want to cycle through the photos, timings provided by Products.jsx for syncing
    if (animationImages?.length > 0) {
      let activeIndex;

      // If the image has less animation frames than the timer count, just show the last one
      if (animationTimerIndex > animationImages.length - 1) {
        activeIndex = animationImages.length - 1;
      } else {
        activeIndex = animationTimerIndex;
      }
      const { msaId, attrs } = animationImages[activeIndex];
      const imageSource = `https://m.media-amazon.com/images/I/${msaId}._${attrs}_SX255_AC_.jpg`;
      imgProps.src = imageSource;
      imgProps.className = css.autoZoom;
      return (
        <>
          <ImageLazyLoader
            forceLoad={forceLoad}
            imgProps={imgProps}
            loadThreshold={LOAD_THRESHOLD}
            placeholder={IMAGE_PLACEHOLDER} />
          {secondaryImage}
        </>
      )
      ;
    }

    return imgProps.src && <>
      <ImageLazyLoader
        forceLoad={forceLoad}
        imgProps={imgProps}
        loadThreshold={LOAD_THRESHOLD}
        placeholder={IMAGE_PLACEHOLDER} />
      {secondaryImage}
    </>;
  };

  makeMiniRelatedStyleImg = (msaImageId, styleColor) => {
    const src = constructMSAImageUrl(msaImageId, RELATED_STYLE_IMG_SRC_PARAMS);
    const retina = constructMSAImageUrl(msaImageId, RELATED_STYLE_IMG_RETINA_PARAMS);
    const srcSet = `${src} 1x, ${retina} 2x`;
    const alt = `Related style: ${styleColor}`;
    return { src, srcSet, alt };
  };

  handleRelatedStyleClick = (swatch, index) => {
    const { swatchClickCallback } = this.props;
    trackEvent('TE_SEARCH_PRODUCT_RELATED_STYLE', `${swatch.styleId}`);
    swatchClickCallback(swatch, index);
  };

  setSelectedRelatedStyleIndex = selectedRelatedStyleIndex => this.setState({ selectedRelatedStyleIndex, renderRelatedStyleLinks: true });

  makeRelatedStylesDropdownLarge = () => {
    const { selectedRelatedStyleIndex, relatedStylesDropdownHasBeenOpened, cachedRelatedStyles } = this.state;
    const { setSelectedRelatedStyleIndex } = this;
    const { shouldLazyLoad, cardData: { productId }, isRelatedStyleDropdownOpen, setRelatedStyleOpenedIndex, index } = this.props;
    const curIndex = selectedRelatedStyleIndex || 0;
    const curItem = cachedRelatedStyles[curIndex];
    const len = cachedRelatedStyles.length;
    const imgProps = this.makeMiniRelatedStyleImg(curItem.msaImageId, curItem.styleColor);
    return (
      <div className={cn(css.relatedStylesDropdownLarge, { [css.relatedStylesDropdownLargeOpen] : isRelatedStyleDropdownOpen })} data-card-related-styles-container>
        <button
          type="button"
          onClick={() => {
            this.setState({ relatedStylesDropdownHasBeenOpened: true });
            setRelatedStyleOpenedIndex(isRelatedStyleDropdownOpen ? null : index);
            if (isRelatedStyleDropdownOpen) {
              trackEvent('TE_SEARCH_RELATED_STYLES_DROPDOWN_CLOSE', productId);
            } else {
              trackEvent('TE_SEARCH_RELATED_STYLES_DROPDOWN_OPEN', productId);
            }
          }}
          aria-label={`Open dropdown for ${len} related styles.`}
          aria-expanded={isRelatedStyleDropdownOpen}>
          <ImageLazyLoader
            forceLoad={!shouldLazyLoad}
            className={cn(css.relatedStyleLazyLoader, { [css.hidden]: isRelatedStyleDropdownOpen })}
            imgProps={imgProps}
            loadThreshold={LOAD_THRESHOLD}/>
          <span className={css.dropdownText}>{isRelatedStyleDropdownOpen ?
            'Select a color' :
            `${len} ${pluralize('color', len)}`}
          </span>
        </button>
        {(isRelatedStyleDropdownOpen || relatedStylesDropdownHasBeenOpened) && <ul
          className={cn({ [css.hidden]: !isRelatedStyleDropdownOpen })}
          onMouseLeave={() => setSelectedRelatedStyleIndex(null)}
          onBlur={() => setSelectedRelatedStyleIndex(null)}>
          {cachedRelatedStyles.map(({ msaImageId, styleColor, originalPrice, price, productSeoUrl }, i) => (
            <li key={productSeoUrl}>
              <Link
                className={css.relatedStyleLink}
                to={productSeoUrl}
                onClick={() => this.handleRelatedStyleClick(cachedRelatedStyles[i], i)}
                onMouseEnter={() => setSelectedRelatedStyleIndex(i)}
                onFocus={() => setSelectedRelatedStyleIndex(i)}>
                <ImageLazyLoader
                  className={css.relatedStyleLazyLoaderSub}
                  imgProps={this.makeMiniRelatedStyleImg(msaImageId, styleColor)}
                  loadThreshold={LOAD_THRESHOLD}
                />
                <div>
                  <p className={css.relatedStyleColorName}>{styleColor}</p>
                  {ProductUtils.isStyleOnSale({ price, originalPrice }) ?
                    <p className={css.dropDownSale}>{price} <span className={css.dropDownMSRP}>MSRP: {originalPrice}</span></p> :
                    <p className={css.dropDownPrice}>{price}</p>}
                </div>
              </Link>
            </li>
          ))}
        </ul>}
      </div>
    );
  };

  makeRelatedStylesDropdownCompact = () => {
    const { renderRelatedStyleLinks, cachedRelatedStyles } = this.state;
    const { setSelectedRelatedStyleIndex } = this;
    const { shouldLazyLoad, cardData: { productId }, isRelatedStyleDropdownOpen, setRelatedStyleOpenedIndex, index } = this.props;

    return (
      <div
        data-card-related-styles-container
        data-related-compact-slide-count={cachedRelatedStyles.length}
        className={cn(css.relatedStylesDropdownCompact, { [css.open]: isRelatedStyleDropdownOpen })}
        onMouseLeave={() => setSelectedRelatedStyleIndex(null)}
        onBlur={() => setSelectedRelatedStyleIndex(null)}>
        <button
          type="button"
          className={css.dropdownCompactButton}
          onClick={() => {
            setRelatedStyleOpenedIndex(isRelatedStyleDropdownOpen ? null : index);
            if (isRelatedStyleDropdownOpen) {
              trackEvent('TE_SEARCH_RELATED_STYLES_DROPDOWN_CLOSE', productId);
            } else {
              trackEvent('TE_SEARCH_RELATED_STYLES_DROPDOWN_OPEN', productId);
            }
          }}
          aria-expanded={isRelatedStyleDropdownOpen}>
          Open dropdown for related styles
        </button>
        {cachedRelatedStyles.map(({ msaImageId, styleColor, productSeoUrl }, i) => (
          <Link
            key={productSeoUrl}
            tabIndex={0}
            to={renderRelatedStyleLinks ? productSeoUrl : null}
            className={css.dropdownCompactLink}
            aria-label={`Alternate style: ${styleColor}`}
            onClick={() => this.handleRelatedStyleClick(cachedRelatedStyles[i], i)}
            onMouseEnter={() => setSelectedRelatedStyleIndex(i)}
            onFocus={() => setSelectedRelatedStyleIndex(i)}>
            <ImageLazyLoader
              forceLoad={!shouldLazyLoad}
              imgProps={this.makeMiniRelatedStyleImg(msaImageId, styleColor)}
              loadThreshold={LOAD_THRESHOLD}/>
          </Link>
        ))}
      </div>
    );
  };

  makeRelatedStyles = () => {
    const { hydraSearchColorSwatches, colorSwatchToggleState } = this.props;
    if (hydraSearchColorSwatches === 0 || !colorSwatchToggleState) {
      return null;
    } else if (hydraSearchColorSwatches === 1) {
      return this.makeRelatedStylesDropdownLarge();
    } else if (hydraSearchColorSwatches === 2) {
      return this.makeRelatedStylesDropdownCompact();
    }
  };

  makeRelatedStylePriceBanner = () => {
    const { hydraSearchColorSwatches } = this.props;
    const { selectedRelatedStyleIndex, cachedRelatedStyles } = this.state;
    const curStyle = cachedRelatedStyles.length && typeof selectedRelatedStyleIndex === 'number' && cachedRelatedStyles[selectedRelatedStyleIndex];
    if (hydraSearchColorSwatches === 2 && curStyle) {
      const { price, originalPrice } = curStyle;
      const isOnSale = ProductUtils.isStyleOnSale({ price, originalPrice });
      return (
        <p className={cn(css.relatedStylePriceDiff, { [css.isSale]: isOnSale })}>
          {curStyle.price}{isOnSale && <span> MSRP: {originalPrice}</span>}
        </p>
      );
    }
  };

  isHearted = () => {
    const { heartsData = {}, cardData: { styleId } } = this.props;
    const { showFavoriteHeart, hearts = [] } = heartsData;
    return isProductHearted(showFavoriteHeart, hearts, styleId);
  };

  handleCollectionToggle = isShowing => {
    this.setState({ isCollectionShown: isShowing });
  };

  handleHeartClick = () => {
    const {
      heartsData = {},
      cardData: style,
      cardData: { productId }
    } = this.props;

    const { onHeartClick, hearts, showFavoriteHeart } = heartsData;
    return makeHandleHeartButtonClick({
      hearts,
      onHeartClick,
      onCollectionToggle: this.handleCollectionToggle,
      productId,
      showFavoriteHeart,
      style
    });
  };

  makeFavoriteHeart = () => {
    const {
      heartsData,
      cardData: style,
      cardData: { productId },
      extraRecoStyle = null
    } = this.props;

    const { testId } = this.context;

    const { hearts, heartsList, showFavoriteHeart } = heartsData || {};

    return makeFavoriteHeartButton({
      cssHeartContainer: css.heartContainer,
      cssHeartActive: css.heartActive,
      extraRecoStyle,
      handleHeartClick: this.handleHeartClick,
      hearts,
      heartsList,
      productId,
      showFavoriteHeart,
      style,
      testId: testId('searchHeart')
    });
  };

  handleClick = e => {
    const { onComponentClick } = this.props;
    if (typeof onComponentClick === 'function') {
      onComponentClick(e, this.props);
    }
  };

  makeProductLabel = () => {
    const { cardData: { price, styleColor, productRating, reviewCount }, hydraSearchColorSwatches } = this.props;
    const productName = this.makeProductNameText();
    const brandName = this.makeBrandNameText();

    let label = '';
    if (productName) {
      label = `${productName}. `;
    }
    if (brandName) {
      label += `By ${brandName}. `;
    }
    if (price) {
      label += `${price}. `;
    }
    if (styleColor && !hydraSearchColorSwatches) {
      label += `Style: ${styleColor}. `;
    }
    if (reviewCount && typeof productRating === 'number') {
      label += `Rated ${productRating} out of 5 stars. `;
    }
    return label;
  };

  render() {
    const { testId } = this.context;
    const { cachedRelatedStyles, isProductFocused, hasProductBeenHovered, isCollectionShown } = this.state;
    const { eventLabel, componentStyle, additionalClasses, vertical,
      noBackground, linkElOverride, melodyCardTestId, hideBanner, hydraSearchColorSwatches,
      colorSwatchToggleState, hydraSocialCollections,
      hydraPDPProductAngles, crossSite,
      cardData: { imageMap, productSeoUrl, productUrlRelative, link, styleId },
      heartsData = {} } = this.props;
    // Only add event attributes if we need them
    const { showFavoriteHeart = false } = heartsData || {};
    const dataAttributes = {};
    if (eventLabel) {
      dataAttributes['data-eventlabel'] = eventLabel;
      dataAttributes['data-eventvalue'] = this.makeEventValue();
    }

    // Reassign Link element if necessary
    const LinkComponent = linkElOverride || Link;

    // For swatches we want main product URLs to be free of the color ID. But when we hover/focus it
    // we want to use the color ID. So users get the color but SEO bots hopefully won't.
    const url = hydraSearchColorSwatches && cachedRelatedStyles.length && !hasProductBeenHovered ?
      buildSeoProductUrl(this.props.cardData) : productSeoUrl || productUrlRelative || link;
    const moreThanOneImageInMap = !!this.getSecondaryImageFromImageMap(imageMap);
    return (
      <article
        className={cn(
          commonCardCss.mCard,
          css.productCardShared,
          additionalClasses.container,
          getComponentStyling(componentStyle, showFavoriteHeart, hydraSearchColorSwatches, colorSwatchToggleState, cachedRelatedStyles, crossSite),
          { [css.vertical]: vertical,
            [css.productIsFocused]: isProductFocused
          }
        )}
        itemScope
        itemType="http://schema.org/Product"
        data-test-id={testId(melodyCardTestId)}>
        <LinkComponent
          to={url}
          onClick={this.handleClick}
          aria-label={this.makeProductLabel()}
          // For helping site merch collect styleIds https://github01.zappos.net/mweb/marty/pull/9699
          data-style-id={styleId}
          data-test-id={testId(`${melodyCardTestId}Link`)}
          itemProp="url"
          onMouseOver={() => this.setState({ hasProductBeenHovered: true })}
          onBlur={() => this.setState({ isProductFocused: false })}
          onFocus={() => this.setState({ isProductFocused: true, hasProductBeenHovered: true })}
          className={css.link}
          {...dataAttributes}>
          <meta itemProp="image" content={this.makeImageUrl()} />
          <div className={cn(
            commonCardCss.image,
            {
              [commonCardCss.imageNoBackground]: noBackground,
              [css.hoverContainer]: moreThanOneImageInMap && (hydraPDPProductAngles === 1),
              [css.fadingHoverContainer]: moreThanOneImageInMap && (hydraPDPProductAngles === 2)
            },
            css.productImage)}>
            {!hideBanner && this.makeProductBanner()}
            {this.makeRelatedStylePriceBanner()}
            {this.makeProductImage()}
          </div>
        </LinkComponent>
        <div className={cn(css.productContent, additionalClasses.textContainer)}>
          {this.makeRelatedStyles()}
          {hydraSocialCollections && <MakeSocialCollectionWidget styleId={styleId} isCollectionShown={isCollectionShown} />}
          {this.makeFavoriteHeart()}
          <CrossSiteLabel store={crossSite}/>
          {this.makeBrandName()}
          {this.makeProductName()}
          {this.makeColorName()}
          {this.makePrice()}
          {this.makeProductRating()}
        </div>
      </article>
    );
  }
}

MelodyCardProduct.propTypes = {
  cardData: PropTypes.object.isRequired,
  eventLabel: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  onComponentClick: PropTypes.func,
  additionalClasses: PropTypes.shape({
    container: PropTypes.string,
    image: PropTypes.string,
    textContainer: PropTypes.string,
    priceContainer: PropTypes.string
  })
};

MelodyCardProduct.contextTypes = {
  testId: PropTypes.func
};

MelodyCardProduct.defaultProps = {
  additionalClasses: {},
  componentStyle: defaultComponentStyle,
  eventLabel: false,
  noBackground: defaultNoBackground
};

export default withErrorBoundary('MelodyCardProduct', MelodyCardProduct);
